import React, { useState, useEffect, useRef, useMemo, Component } from 'react';
import axios from 'axios';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Card } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { RadioGroup, RadioGroupItem } from '@/components/ui/radio-group';
import GoogleMapsAddressInput from '@/components/GoogleMapsAddressInput';
import { readLocalCustomer, isLocalCustomerComplete, saveLocalCustomer } from '@/lib/localCustomer';
import { normalizeCustomerPhone } from '@/lib/phone';
import { CheckoutLoyaltySummary } from '@/components/loyalty/CheckoutLoyaltySummary';
import { readOrderNotes, clearOrderNotes } from '@/lib/orderNotesStorage';
import CheckoutTipSection from '@/pages/menu/checkout/CheckoutTipSection';
import { clearCheckoutChatContext } from '@/lib/checkoutChatContext';
import { requestMenuDeliveryQuote } from '@/lib/checkoutDeliveryQuote';
import { detectDeliveryZone, hasDeliveryZones } from '@/lib/deliveryZones';
import { uploadMenuOrderProof } from '@/lib/checkoutProofUpload';
import { submitCheckoutToWhatsApp } from '@/lib/checkoutSubmitOrder';
import {
  CHECKOUT_PAYMENT_LABELS,
  getCheckoutPaymentLabel,
  getEnabledPaymentMethodsForCheckout,
  selectionToCanonicalPayment,
} from '@/lib/checkoutPaymentMethods';
import {
  parseCashAmountInput,
  openWhatsAppUrl,
  getBusinessWhatsappDigits,
} from '@/lib/checkoutWhatsAppOrder';
import { X, Loader2, MapPin, CreditCard, Banknote, Smartphone, Upload, Copy, Trash2, Minus, Plus } from 'lucide-react';
import { toast } from 'sonner';
import { Link } from 'react-router-dom';
import { logStructured } from '@/lib/networkLog';
import { getPublicMenuPath } from '@/lib/tenant/getPublicMenuPath';
import { trackEvent } from '@/lib/analytics/trackEvent';
import { absoluteCheckoutTrackingUrl } from '@/lib/checkoutTrackingUrl';
import {
  getMenuTableCheckoutPatch,
} from '@/lib/menu/menuTableContext';
import {
  resolveCheckoutBootstrapStep,
  getStoredCheckoutDefaults,
  shouldOfferCheckoutDataPreference,
  resolveCheckoutEntryStep,
  resolveCheckoutStepAfterCart,
  resolvePreferredServiceTypeFromHistory,
  hasPreviousOrderForBusiness,
} from '@/lib/assistant/assistantOrderCloseFlow';

const BANK_ACCOUNT_TYPE_LABELS = {
  corriente: 'Cuenta Corriente',
  vista: 'Cuenta Vista',
  ahorro: 'Cuenta de Ahorro',
};

function labelBankAccountType(value) {
  if (value == null || String(value).trim() === '') return '—';
  const key = String(value).toLowerCase().trim();
  return BANK_ACCOUNT_TYPE_LABELS[key] ?? String(value);
}

function goToClassicCheckout(navigate, publicMenuRouteBusinessId) {
  const path = getPublicMenuPath(publicMenuRouteBusinessId ?? '', 'checkout');
  if (typeof navigate === 'function') {
    try {
      navigate(path);
      return;
    } catch {
      /* fall through */
    }
  }
  window.location.assign(path);
}

function AssistantCheckoutFallback({
  businessId,
  publicMenuRouteBusinessId = '',
  navigate,
  onClose,
  primaryColor,
  classicCheckoutButtonLabel = 'Ir al checkout clásico',
  title = 'Seguí tu pedido en el checkout',
  message = 'No pudimos mostrar el checkout aquí. Podés completarlo en la página de checkout del menú.',
}) {
  const canGo = Boolean(businessId);
  return (
    <div className="fixed inset-x-0 bottom-0 z-[70] max-h-[85vh] overflow-y-auto border-t bg-background text-foreground shadow-2xl rounded-t-xl">
      <div className="max-w-4xl mx-auto px-4 py-5 space-y-4">
        <div className="flex items-start justify-between gap-2">
          <h2 className="text-base font-semibold">{title}</h2>
          <Button type="button" variant="ghost" size="icon" onClick={() => onClose?.()} aria-label="Cerrar">
            <X className="h-4 w-4" />
          </Button>
        </div>
        <p className="text-sm text-muted-foreground">{message}</p>
        <div className="flex flex-col gap-2">
          {canGo ? (
            <Button
              type="button"
              className="w-full text-white"
              style={{ backgroundColor: primaryColor }}
              onClick={() => {
                onClose?.();
                goToClassicCheckout(navigate, publicMenuRouteBusinessId);
              }}
            >
              {classicCheckoutButtonLabel}
            </Button>
          ) : null}
          <Button type="button" variant="outline" className="w-full" onClick={() => onClose?.()}>
            Cerrar
          </Button>
        </div>
      </div>
    </div>
  );
}

class AssistantCheckoutFlowBoundary extends Component {
  state = { error: null };

  static getDerivedStateFromError() {
    return { error: true };
  }

  componentDidUpdate(prevProps) {
    if (!prevProps.open && this.props.open) {
      this.setState({ error: null });
    }
  }

  render() {
    if (this.state.error) {
      return (
        <AssistantCheckoutFallback
          businessId={this.props.businessId}
          publicMenuRouteBusinessId={this.props.publicMenuRouteBusinessId}
          navigate={this.props.navigate}
          onClose={this.props.onClose}
          primaryColor={this.props.primaryColor}
          classicCheckoutButtonLabel={this.props.classicCheckoutButtonLabel}
          title="Algo salió mal"
          message="Ocurrió un error al mostrar el checkout. Podés completar tu pedido en la página de checkout del menú."
        />
      );
    }
    return this.props.children;
  }
}

function paymentMethodToSelection(method) {
  const k = String(method || '').toLowerCase();
  if (k === 'transferencia' || k.includes('transfer')) return 'transferencia';
  if (k === 'online_manual') return 'online_card';
  if (k.includes('mercado')) return 'online_mp';
  return 'efectivo';
}

function CheckoutCartLinesEditor({
  cartLines,
  updateQuantity,
  removeFromCart,
  removeAddOnFromCartLine,
}) {
  return (
    <div className="rounded-md bg-muted/50 p-3 space-y-3 max-h-72 overflow-y-auto">
      {cartLines.map((item) => {
        const q = Math.max(1, Math.min(99, Number(item.quantity) || 1));
        const lineTotal = (Number(item.price) || 0) * q;
        const reward = Boolean(item.isClubReward);
        return (
          <div key={item.id} className="border-b border-border/60 pb-3 last:border-0">
            <div className="flex items-start justify-between gap-2">
              <div className="min-w-0 flex-1">
                <p className="font-medium leading-tight">{item.product?.name}</p>
                {reward ? (
                  <Badge variant="secondary" className="mt-1 text-[10px]">
                    Premio canjeado
                  </Badge>
                ) : null}
              </div>
              <div className="flex shrink-0 items-center gap-0.5">
                <Button
                  type="button"
                  variant="outline"
                  size="icon"
                  className="h-8 w-8"
                  disabled={reward}
                  onClick={() => updateQuantity(item.id, q - 1)}
                  aria-label="Menos cantidad"
                >
                  <Minus className="h-4 w-4" />
                </Button>
                <span className="w-8 text-center text-sm tabular-nums">{q}</span>
                <Button
                  type="button"
                  variant="outline"
                  size="icon"
                  className="h-8 w-8"
                  disabled={reward || q >= 99}
                  onClick={() => updateQuantity(item.id, q + 1)}
                  aria-label="Más cantidad"
                >
                  <Plus className="h-4 w-4" />
                </Button>
                <Button
                  type="button"
                  variant="ghost"
                  size="icon"
                  className="h-8 w-8 text-destructive hover:text-destructive"
                  disabled={reward}
                  title={
                    reward
                      ? 'El premio ya fue canjeado; no se puede quitar desde acá.'
                      : undefined
                  }
                  onClick={() => {
                    removeFromCart(item.id, { silent: true });
                    toast.message('Producto quitado del pedido');
                  }}
                  aria-label="Quitar producto del pedido"
                >
                  <Trash2 className="h-4 w-4" />
                </Button>
              </div>
            </div>
            <p className="mt-1 text-xs text-muted-foreground tabular-nums">
              {reward ? '🎁 Subtotal premio: $0' : `Subtotal: $${lineTotal.toLocaleString('es-CL')}`}
            </p>
            {item.addOns?.length ? (
              <ul className="mt-2 space-y-1.5 text-xs text-muted-foreground">
                {item.addOns.map((a, i) => (
                  <li key={`${item.id}-ao-${i}`} className="flex items-center justify-between gap-2">
                    <span className="min-w-0">
                      + {a.name}
                      {Number(a.quantity) > 1 ? ` ×${a.quantity}` : ''}
                      {Number(a.price) > 0 ? ` ($${Number(a.price).toLocaleString('es-CL')})` : ''}
                    </span>
                    <Button
                      type="button"
                      variant="ghost"
                      size="sm"
                      className="h-7 shrink-0 px-2 text-destructive hover:text-destructive"
                      onClick={() => removeAddOnFromCartLine(item.id, i)}
                    >
                      Quitar
                    </Button>
                  </li>
                ))}
              </ul>
            ) : null}
            {item.removedIngredients?.length ? (
              <p className="mt-1 text-xs">Sin: {item.removedIngredients.join(', ')}</p>
            ) : null}
          </div>
        );
      })}
    </div>
  );
}

/**
 * Checkout conversacional embebido en el asistente.
 * Reutiliza delivery-quote, upload de comprobante y submitCheckoutToWhatsApp (mismo que CheckoutPage).
 */
function AssistantCheckoutFlowInner({
  open,
  onClose,
  businessId,
  publicMenuRouteBusinessId = '',
  business,
  cart,
  getCartTotal,
  clearCart,
  removeFromCart,
  updateQuantity,
  removeAddOnFromCartLine,
  navigate,
  primaryColor = '#10b981',
  analyticsSource = 'assistant',
  checkoutSource = 'assistant_checkout',
  classicCheckoutLabel = 'checkout clásico',
  classicCheckoutButtonLabel = 'Ir al checkout clásico',
  title = 'Completa tus datos para terminar el pedido',
  checkoutBootstrap = null,
}) {
  const serviceTypes = business?.service_types ?? [];

  const [step, setStep] = useState('service');
  const [serviceType, setServiceType] = useState(() =>
    serviceTypes.includes('delivery') ? 'delivery' : serviceTypes.includes('retiro') ? 'retiro' : 'mesa'
  );
  const [tableNumber, setTableNumber] = useState('');
  const [customerName, setCustomerName] = useState('');
  const [customerPhone, setCustomerPhone] = useState('');
  const [customerEmail, setCustomerEmail] = useState('');
  const [address, setAddress] = useState('');
  const [markerPosition, setMarkerPosition] = useState(null);
  const [deliveryQuote, setDeliveryQuote] = useState(null);
  const [deliveryLoading, setDeliveryLoading] = useState(false);
  const [paymentSelection, setPaymentSelection] = useState('efectivo');
  const [cashAmount, setCashAmount] = useState('');
  const [tip, setTip] = useState(0);
  const [proofFile, setProofFile] = useState(null);
  const [proofPreview, setProofPreview] = useState(null);
  const [proofUrl, setProofUrl] = useState(null);
  const [uploadingProof, setUploadingProof] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [orderPlaced, setOrderPlaced] = useState(null);

  const deliveryQuoteGenRef = useRef(0);
  const deliveryDebounceRef = useRef(null);
  const deliveryAbortRef = useRef(null);
  const zoneDebounceRef = useRef(null);
  const proofInputRef = useRef(null);
  const assistantTrackingShownRef = useRef(false);
  const bootstrapAppliedRef = useRef(false);
  const sendingRef = useRef(false);
  const idempotencyKeyRef = useRef(null);

  const cartLines = Array.isArray(cart) ? cart : [];

  const useAddressZonesForDelivery = useMemo(
    () => hasDeliveryZones(business),
    [business?.delivery_zones]
  );

  useEffect(() => {
    if (!orderPlaced?.trackingUrl || assistantTrackingShownRef.current) return;
    assistantTrackingShownRef.current = true;
    try {
      trackEvent('checkout_order_tracking_shown', {});
    } catch {
      /* noop */
    }
  }, [orderPlaced?.trackingUrl]);

  useEffect(() => {
    if (!open || !businessId) return;
    logStructured('analytics', {
      event: 'checkout_opened',
      businessId,
      source: analyticsSource,
    });
  }, [open, businessId, analyticsSource]);

  useEffect(() => {
    if (!open) {
      assistantTrackingShownRef.current = false;
      bootstrapAppliedRef.current = false;
      setOrderPlaced(null);
      return;
    }
    const c = readLocalCustomer(businessId);
    const boot = checkoutBootstrap;
    const order = boot?.order;

    if (boot?.mode === 'same' || boot?.mode === 'change') {
      if (!bootstrapAppliedRef.current) {
        bootstrapAppliedRef.current = true;
        const stRaw = order?.serviceType || order?.service_type || '';
        const st =
          stRaw === 'delivery' || stRaw === 'retiro' || stRaw === 'mesa'
            ? stRaw
            : serviceTypes.includes('delivery')
              ? 'delivery'
              : serviceTypes.includes('retiro')
                ? 'retiro'
                : 'mesa';
        setServiceType(st);
        setCustomerName(order?.customerName || c?.name || '');
        setCustomerPhone(order?.customerPhone || c?.phone || '');
        setCustomerEmail(c?.email || '');
        const addr = order?.address || c?.address || '';
        if (addr) setAddress(String(addr));
        if (order?.markerPosition?.lat != null) setMarkerPosition(order.markerPosition);
        if (order?.tableNumber) setTableNumber(String(order.tableNumber));
        setPaymentSelection(paymentMethodToSelection(order?.paymentMethod));
        if (order?.cash_amount != null) setCashAmount(String(order.cash_amount));
        if (order?.tip != null) setTip(Math.max(0, Number(order.tip) || 0));
        setProofFile(null);
        setProofPreview(null);
        setProofUrl(null);
        setStep(
          boot.initialStep ||
            (boot.mode === 'same'
              ? 'review'
              : resolveCheckoutBootstrapStep(order, c, serviceTypes))
        );
        return;
      }
    }

    if (boot?.initialStep === 'cart') {
      if (!bootstrapAppliedRef.current) {
        bootstrapAppliedRef.current = true;
        if (isLocalCustomerComplete(c)) {
          setCustomerName(c.name || '');
          setCustomerPhone(c.phone || '');
          setCustomerEmail(c.email || '');
          if (c.address) setAddress(String(c.address));
        }
        const tablePatch = getMenuTableCheckoutPatch(businessId, business);
        if (tablePatch) {
          setServiceType('mesa');
          setTableNumber(tablePatch.tableNumber);
        } else if (serviceTypes.includes('delivery')) {
          setServiceType('delivery');
        } else if (serviceTypes.includes('retiro')) {
          setServiceType('retiro');
        } else {
          setServiceType('mesa');
        }
        setMarkerPosition(null);
        setDeliveryQuote(null);
        setProofFile(null);
        setProofPreview(null);
        setProofUrl(null);
        setCashAmount('');
        setTip(0);
        setPaymentSelection('efectivo');
        if (!tablePatch) setTableNumber('');
        setStep('cart');
        return;
      }
    }

    const tablePatch = getMenuTableCheckoutPatch(businessId, business);

    if (isLocalCustomerComplete(c)) {
      setCustomerName(c.name || '');
      setCustomerPhone(c.phone || '');
      setCustomerEmail(c.email || '');
      if (c.address) setAddress(String(c.address));
    }
    const entryStep = resolveCheckoutEntryStep(businessId, serviceTypes, business);
    const preferredService = tablePatch
      ? 'mesa'
      : hasPreviousOrderForBusiness(businessId)
        ? resolvePreferredServiceTypeFromHistory(businessId, serviceTypes, business)
        : serviceTypes.includes('delivery')
          ? 'delivery'
          : serviceTypes.includes('retiro')
            ? 'retiro'
            : 'mesa';
    setServiceType(preferredService);
    setStep(entryStep);
    setMarkerPosition(null);
    setDeliveryQuote(null);
    setProofFile(null);
    setProofPreview(null);
    setProofUrl(null);
    setCashAmount('');
    setTip(0);
    setPaymentSelection('efectivo');
    setTableNumber(tablePatch?.tableNumber || '');
  }, [open, businessId, serviceTypes.join(','), checkoutBootstrap, business]);

  useEffect(() => {
    if (open && step === 'cart' && cartLines.length === 0) {
      onClose?.();
    }
  }, [open, step, cartLines.length, onClose]);

  useEffect(() => {
    if (!open || serviceType !== 'delivery' || useAddressZonesForDelivery) {
      if (deliveryDebounceRef.current) clearTimeout(deliveryDebounceRef.current);
      deliveryAbortRef.current?.abort();
      return;
    }
    if (!markerPosition || typeof markerPosition.lat !== 'number') {
      setDeliveryQuote(null);
      setDeliveryLoading(false);
      return;
    }

    const { lat, lng } = markerPosition;
    if (typeof lng !== 'number' || Number.isNaN(lat) || Number.isNaN(lng)) return;

    if (deliveryDebounceRef.current) clearTimeout(deliveryDebounceRef.current);
    deliveryDebounceRef.current = setTimeout(() => {
      deliveryDebounceRef.current = null;
      deliveryAbortRef.current?.abort();
      const ac = new AbortController();
      deliveryAbortRef.current = ac;
      const myGen = ++deliveryQuoteGenRef.current;
      setDeliveryLoading(true);
      (async () => {
        try {
          const data = await requestMenuDeliveryQuote(businessId, lat, lng, { signal: ac.signal });
          if (myGen !== deliveryQuoteGenRef.current) return;
          if (!data.within_radius) {
            toast.error(data.detail || 'Fuera del radio de cobertura');
            setMarkerPosition(null);
            setDeliveryQuote(null);
            return;
          }
          setDeliveryQuote({
            delivery_fee: typeof data.delivery_fee === 'number' ? data.delivery_fee : null,
            distance_km: typeof data.distance_km === 'number' ? data.distance_km : null,
            within_radius: data.within_radius !== false,
            distance_source: data.distance_source ?? null,
          });
        } catch (e) {
          if (axios.isCancel?.(e) || e.code === 'ERR_CANCELED' || e.name === 'CanceledError') return;
          if (myGen !== deliveryQuoteGenRef.current) return;
          const msg = e.response?.data?.detail;
          toast.error(typeof msg === 'string' ? msg : 'No se pudo calcular el delivery');
          setDeliveryQuote(null);
        } finally {
          if (myGen === deliveryQuoteGenRef.current) setDeliveryLoading(false);
        }
      })();
    }, 950);

    return () => {
      if (deliveryDebounceRef.current) clearTimeout(deliveryDebounceRef.current);
      deliveryAbortRef.current?.abort();
    };
  }, [open, serviceType, useAddressZonesForDelivery, markerPosition?.lat, markerPosition?.lng, businessId]);

  useEffect(() => {
    if (!open || !useAddressZonesForDelivery || serviceType !== 'delivery' || !business) {
      if (zoneDebounceRef.current) {
        clearTimeout(zoneDebounceRef.current);
        zoneDebounceRef.current = null;
      }
      if (serviceType !== 'delivery' || !open) {
        setDeliveryQuote(null);
      }
      return;
    }

    if (zoneDebounceRef.current) {
      clearTimeout(zoneDebounceRef.current);
      zoneDebounceRef.current = null;
    }
    const addr = String(address || '').trim();
    if (!addr) {
      setDeliveryQuote(null);
      return;
    }
    zoneDebounceRef.current = setTimeout(() => {
      zoneDebounceRef.current = null;
      const zone = detectDeliveryZone(address, business.delivery_zones);
      if (zone) {
        const price = Number(zone.price);
        const mins = Number(zone.estimated_minutes);
        setDeliveryQuote({
          source: 'zone',
          within_radius: true,
          no_zone_match: false,
          delivery_fee: Number.isFinite(price) ? price : 0,
          estimated_minutes: Number.isFinite(mins) ? mins : 0,
          zone_name: String(zone.name || '').trim() || 'Zona',
          distance_km: null,
          distance_source: 'zone',
        });
      } else if (addr.length > 2) {
        setDeliveryQuote({
          source: 'zone',
          within_radius: false,
          no_zone_match: true,
          delivery_fee: null,
          distance_km: null,
          distance_source: 'zone',
        });
      } else {
        setDeliveryQuote(null);
      }
    }, 500);
    return () => {
      if (zoneDebounceRef.current) {
        clearTimeout(zoneDebounceRef.current);
        zoneDebounceRef.current = null;
      }
    };
  }, [open, useAddressZonesForDelivery, serviceType, address, business]);

  const { canonical: canonicalPaymentMethod, onlineManualKind: paymentOnlineManualKind } = useMemo(
    () => selectionToCanonicalPayment(paymentSelection),
    [paymentSelection]
  );

  useEffect(() => {
    if (paymentSelection !== 'efectivo') setCashAmount('');
  }, [paymentSelection]);

  const { methodIds: enabledPaymentMethods, usedFallback: paymentMethodsFallback } = useMemo(
    () => getEnabledPaymentMethodsForCheckout(business),
    [
      business?.accepts_cash,
      business?.accepts_transfer,
      business?.accepts_card,
      business?.accepts_mercadopago,
      business?.mercadopago_online,
    ]
  );

  useEffect(() => {
    if (!enabledPaymentMethods.length) return;
    if (!enabledPaymentMethods.includes(paymentSelection)) {
      setPaymentSelection(enabledPaymentMethods[0]);
    }
  }, [enabledPaymentMethods, paymentSelection]);

  if (!open) return null;

  if (orderPlaced) {
    const digits = getBusinessWhatsappDigits(business);
    const telHref = digits.length >= 8 ? `tel:+${digits}` : null;
    return (
      <div className="fixed inset-x-0 bottom-0 z-[70] max-h-[85vh] overflow-y-auto rounded-t-xl border-t border-zinc-200 bg-white text-zinc-950 shadow-2xl dark:border-zinc-800 dark:bg-zinc-950 dark:text-zinc-50">
        <div className="max-w-4xl mx-auto px-4 py-5 space-y-4">
          <h2 className="text-base font-semibold text-zinc-950 dark:text-zinc-50">Pedido registrado</h2>
          <div className="space-y-2 text-sm">
            <p className="text-zinc-900 dark:text-zinc-100">Tu pedido quedó registrado correctamente en el local.</p>
            {orderPlaced.whatsappUnavailable ? (
              <p className="text-amber-700 dark:text-amber-400 font-medium">
                Este local no tiene WhatsApp configurado. Podés copiar el resumen o contactar al local por otro medio.
              </p>
            ) : (
              <p className="text-zinc-600 dark:text-zinc-300">
                Abrí WhatsApp con el resumen o usá copiar pedido si preferís otro canal.
              </p>
            )}
            {orderPlaced.orderId ? (
              <p className="text-xs text-zinc-600 dark:text-zinc-300">
                Referencia: <span className="font-mono">{orderPlaced.orderId}</span>
              </p>
            ) : null}
            {typeof orderPlaced.totalSnap === 'number' ? (
              <p className="font-medium tabular-nums text-zinc-950 dark:text-zinc-50">
                Total: ${orderPlaced.totalSnap.toLocaleString('es-CL')}
              </p>
            ) : null}
          </div>
          {orderPlaced.orderTextPlain ? (
            <div className="space-y-1">
              <Label className="text-xs text-zinc-600 dark:text-zinc-300">Resumen del pedido</Label>
              <Textarea
                readOnly
                value={orderPlaced.orderTextPlain}
                className="min-h-[100px] max-h-[180px] resize-y border-zinc-300 bg-white text-xs font-mono text-zinc-950 shadow-inner dark:border-zinc-700 dark:bg-zinc-900 dark:text-zinc-50"
              />
            </div>
          ) : null}
          <div className="flex flex-col gap-2">
            {orderPlaced.trackingUrl ? (
              <div className="rounded-lg border border-zinc-200 bg-zinc-50 p-4 space-y-3 dark:border-zinc-800 dark:bg-zinc-900" data-testid="assistant-order-tracking-block">
                <h3 className="text-sm font-semibold text-zinc-950 dark:text-zinc-50">Seguimiento del pedido</h3>
                <p className="text-xs text-zinc-600 dark:text-zinc-300 leading-relaxed">
                  Puedes revisar el estado de tu pedido desde este enlace.
                </p>
                <div className="flex flex-col gap-2">
                  <Button asChild variant="default" className="w-full">
                    <Link
                      to={orderPlaced.trackingUrl}
                      state={{ fromCheckoutMenuBusinessId: publicMenuRouteBusinessId || businessId }}
                      onClick={() => {
                        try {
                          trackEvent('checkout_order_tracking_opened', {});
                        } catch {
                          /* noop */
                        }
                      }}
                      data-testid="assistant-order-placed-tracking-open"
                    >
                      Ver estado del pedido
                    </Link>
                  </Button>
                  <Button
                    type="button"
                    variant="outline"
                    className="w-full gap-2 border-zinc-300 bg-white text-zinc-900 hover:bg-zinc-100 dark:border-zinc-700 dark:bg-zinc-900 dark:text-zinc-50 dark:hover:bg-zinc-800"
                    data-testid="assistant-order-placed-tracking-copy"
                    onClick={async () => {
                      try {
                        const abs = absoluteCheckoutTrackingUrl(orderPlaced.trackingUrl);
                        await navigator.clipboard.writeText(abs);
                        toast.success('Enlace copiado');
                        try {
                          trackEvent('checkout_order_tracking_copied', {});
                        } catch {
                          /* noop */
                        }
                      } catch (e) {
                        console.warn('[assistant checkout] clipboard tracking url failed', e);
                        toast.error('No se pudo copiar el enlace.');
                      }
                    }}
                  >
                    <Copy className="w-4 h-4 shrink-0" />
                    Copiar enlace
                  </Button>
                </div>
              </div>
            ) : null}
            {orderPlaced.whatsappUrl ? (
              <>
                <Button
                  type="button"
                  className="w-full text-white"
                  style={{ backgroundColor: primaryColor }}
                  onClick={() => {
                    const navigated = openWhatsAppUrl(orderPlaced.whatsappUrl, {
                      businessId,
                      source: checkoutSource,
                    });
                    if (!navigated) {
                      toast.error('No se pudo abrir WhatsApp. Probá “Copiar pedido”.');
                    }
                  }}
                >
                  Abrir WhatsApp
                </Button>
                {orderPlaced.trackingUrl ? (
                  <p className="text-xs text-zinc-600 dark:text-zinc-300 text-center px-1 leading-snug">
                    También podés copiar el enlace de seguimiento arriba o usar el que va en el mensaje de WhatsApp.
                  </p>
                ) : null}
              </>
            ) : null}
            {orderPlaced.orderTextPlain ? (
              <Button
                type="button"
                variant="outline"
                className="w-full border-zinc-300 bg-white text-zinc-900 hover:bg-zinc-100 dark:border-zinc-700 dark:bg-zinc-900 dark:text-zinc-50 dark:hover:bg-zinc-800"
                onClick={async () => {
                  try {
                    await navigator.clipboard.writeText(orderPlaced.orderTextPlain);
                    toast.success('Pedido copiado al portapapeles');
                  } catch (e) {
                    console.warn('[checkout] clipboard write failed', e);
                    toast.error('No se pudo copiar. Seleccioná el texto manualmente.');
                  }
                }}
              >
                <Copy className="w-4 h-4 mr-2" />
                Copiar pedido
              </Button>
            ) : null}
            {telHref ? (
              <Button type="button" variant="outline" className="w-full border-zinc-300 bg-white text-zinc-900 hover:bg-zinc-100 dark:border-zinc-700 dark:bg-zinc-900 dark:text-zinc-50 dark:hover:bg-zinc-800" asChild>
                <a href={telHref}>Contactar manualmente</a>
              </Button>
            ) : (
              <Button
                type="button"
                variant="outline"
                className="w-full border-zinc-300 bg-white text-zinc-900 hover:bg-zinc-100 dark:border-zinc-700 dark:bg-zinc-900 dark:text-zinc-50 dark:hover:bg-zinc-800"
                onClick={() => navigate(getPublicMenuPath(publicMenuRouteBusinessId, 'home'))}
              >
                Ver menú del local
              </Button>
            )}
            <Button type="button" variant="ghost" className="w-full text-zinc-700 hover:bg-zinc-100 hover:text-zinc-950 dark:text-zinc-200 dark:hover:bg-zinc-900 dark:hover:text-zinc-50" onClick={onClose}>
              Cerrar
            </Button>
          </div>
        </div>
      </div>
    );
  }

  if (!businessId || !business) {
    return (
      <AssistantCheckoutFallback
        businessId={businessId}
        publicMenuRouteBusinessId={publicMenuRouteBusinessId}
        navigate={navigate}
        onClose={onClose}
        primaryColor={primaryColor}
        classicCheckoutButtonLabel={classicCheckoutButtonLabel}
        message={
          !businessId
            ? 'Falta identificar el local. Volvé al menú e intentá de nuevo, o abrí el checkout desde el carrito.'
            : 'La información del local aún no está disponible. Podés completar tu pedido en el checkout del menú.'
        }
      />
    );
  }

  const orderNotes = readOrderNotes(businessId);
  let subtotal = 0;
  try {
    subtotal =
      typeof getCartTotal === 'function' ? Number(getCartTotal()) || 0 : 0;
  } catch {
    subtotal = 0;
  }
  const deliveryLine =
    serviceType === 'delivery' && typeof deliveryQuote?.delivery_fee === 'number'
      ? deliveryQuote.delivery_fee
      : 0;
  const total = subtotal + deliveryLine + (Number(tip) || 0);

  const persistCustomerDraft = () => {
    const phone = normalizeCustomerPhone(customerPhone);
    if (!customerName.trim() || phone.length < 8) return;
    saveLocalCustomer(
      {
        name: customerName.trim(),
        phone,
        email: customerEmail.trim(),
        address: String(address || '').trim(),
      },
      businessId
    );
  };

  const goAfterService = () => {
    if (serviceType === 'mesa' && business?.has_tables !== true && !serviceTypes.includes('mesa')) {
      toast.error('Este local no indica mesa en el menú');
      return;
    }
    if (shouldOfferCheckoutDataPreference(businessId, serviceType)) {
      setStep('data-preference');
      return;
    }
    setStep('customer');
  };

  const continueFromCart = () => {
    const tablePatch = getMenuTableCheckoutPatch(businessId, business);
    if (tablePatch) {
      setServiceType('mesa');
      setTableNumber(tablePatch.tableNumber);
    }
    setStep(resolveCheckoutStepAfterCart(businessId, serviceTypes, business));
  };

  const applySavedCheckoutDefaults = () => {
    const defaults = getStoredCheckoutDefaults(businessId, serviceType);
    if (!defaults) {
      setStep('customer');
      return;
    }
    setCustomerName(defaults.customerName);
    setCustomerPhone(defaults.customerPhone);
    setCustomerEmail(defaults.customerEmail || '');
    if (serviceType === 'delivery') {
      setAddress(defaults.address || '');
      setMarkerPosition(defaults.markerPosition || null);
      if (!defaults.markerPosition) setDeliveryQuote(null);
    }
    if (serviceType === 'mesa') setTableNumber(defaults.tableNumber || '');
    setPaymentSelection(paymentMethodToSelection(defaults.paymentMethod));
    if (defaults.cash_amount != null && Number(defaults.cash_amount) > 0) {
      setCashAmount(String(defaults.cash_amount));
    } else {
      setCashAmount('');
    }
    setTip(defaults.tip != null ? Math.max(0, Number(defaults.tip) || 0) : 0);
    setProofFile(null);
    setProofPreview(null);
    setProofUrl(null);
    saveLocalCustomer(
      {
        name: defaults.customerName,
        phone: defaults.customerPhone,
        email: defaults.customerEmail,
        address: serviceType === 'delivery' ? defaults.address : '',
      },
      businessId
    );
    setStep('review');
  };

  const goAfterCustomer = () => {
    if (!customerName.trim()) {
      toast.error('Ingresá tu nombre');
      return;
    }
    const ph = normalizeCustomerPhone(customerPhone);
    if (ph.length < 8) {
      toast.error('Ingresá un teléfono válido');
      return;
    }
    persistCustomerDraft();
    if (serviceType === 'delivery') setStep('address');
    else if (serviceType === 'mesa') setStep('mesa');
    else setStep('payment');
  };

  const goAfterAddress = () => {
    if (!address.trim()) {
      toast.error('Ingresá tu dirección');
      return;
    }
    if (useAddressZonesForDelivery) {
      if (deliveryQuote?.no_zone_match) {
        toast.error('No tenemos cobertura para esta zona');
        return;
      }
      if (typeof deliveryQuote?.delivery_fee !== 'number') {
        toast.error('Escribí calle, número o comuna para ver cobertura y costo de envío');
        return;
      }
      persistCustomerDraft();
      setStep('payment');
      return;
    }
    if (!markerPosition) {
      toast.error('Confirmá el pin en el mapa');
      return;
    }
    if (deliveryLoading) {
      toast.error('Esperá el cálculo del envío');
      return;
    }
    if (typeof deliveryQuote?.delivery_fee !== 'number') {
      toast.error('No se pudo calcular el envío');
      return;
    }
    persistCustomerDraft();
    setStep('payment');
  };

  const goAfterMesa = () => {
    if (!String(tableNumber || '').trim()) {
      toast.error('Ingresá el número de mesa');
      return;
    }
    setStep('payment');
  };

  const goAfterPayment = () => {
    if (paymentSelection === 'transferencia') {
      setStep('proof');
      return;
    }
    setStep('review');
  };

  const clearProofUpload = () => {
    setProofFile(null);
    setProofPreview(null);
    setProofUrl(null);
    if (proofInputRef.current) proofInputRef.current.value = '';
  };

  const copyBankAccountNumber = () => {
    if (business?.bank_account_number) {
      navigator.clipboard.writeText(business.bank_account_number);
      toast.success('Número de cuenta copiado');
    }
  };

  const handleProofSelect = async (e) => {
    const input = e.target;
    const file = input.files?.[0];
    if (!file) return;
    setProofFile(file);
    const reader = new FileReader();
    reader.onloadend = () => setProofPreview(reader.result);
    reader.onerror = () => {
      toast.error('No se pudo leer la imagen. Probá con otra foto.');
      clearProofUpload();
    };
    reader.readAsDataURL(file);
    setUploadingProof(true);
    setProofUrl(null);
    try {
      const url = await uploadMenuOrderProof(file, {
        businessId,
        customer: {
          name: customerName,
          phone: customerPhone,
          email: customerEmail,
          address,
        },
      });
      setProofUrl(url);
      toast.success('Comprobante cargado');
    } catch (err) {
      console.error(err);
      if (err?.name === 'AbortError') {
        toast.error('La subida tardó demasiado. Probá de nuevo.');
      } else {
        toast.error(err?.message || 'No se pudo subir el comprobante. Revisá tu conexión y reintentá.');
      }
      clearProofUpload();
    } finally {
      setUploadingProof(false);
      if (input) input.value = '';
    }
  };

  /** El resumen ya es la confirmación: un clic envía (evita AlertDialog detrás del sheet z-70). */
  const confirmOrder = async () => {
    if (sendingRef.current || submitting) return;
    sendingRef.current = true;
    setSubmitting(true);
    try {
      if (!idempotencyKeyRef.current) {
        idempotencyKeyRef.current =
          typeof crypto !== 'undefined' && typeof crypto.randomUUID === 'function'
            ? crypto.randomUUID()
            : `${Date.now()}-${Math.random().toString(36).slice(2, 12)}`;
      }
      const result = await submitCheckoutToWhatsApp({
        businessId,
        business,
        cart: cartLines,
        getCartTotal,
        customerName,
        customerPhone,
        customerEmail,
        serviceType,
        tableNumber,
        address,
        markerPosition,
        orderNotes,
        tip: Number(tip) || 0,
        paymentMethod: canonicalPaymentMethod,
        onlineManualKind: paymentOnlineManualKind,
        cashAmount,
        proofUrl,
        proofFile,
        deliveryQuote,
        deliveryLoading,
        useAddressZonesForDelivery,
        checkoutReferralCode: '',
        checkoutRewardChoice: 'none',
        referralInfo: null,
        benefitsProgress: null,
        checkoutSource,
        idempotencyKey: idempotencyKeyRef.current,
        notifyError: (msg) => toast.error(msg),
        notifySuccess: (msg) => toast.success(msg),
      });
      if (!result.ok) {
        toast.error(
          result.error ||
            (result.duplicate
              ? 'Tu pedido ya se está enviando. No pulses otra vez.'
              : 'No se pudo registrar el pedido. Intenta nuevamente.'),
        );
        return;
      }
      idempotencyKeyRef.current = null;
      if (result.mercadopagoCheckoutUrl) {
        clearOrderNotes(businessId);
        clearCheckoutChatContext();
        clearCart();
        toast.success('Redirigiendo a Mercado Pago…');
        window.location.href = result.mercadopagoCheckoutUrl;
        return;
      }
      clearOrderNotes(businessId);
      clearCheckoutChatContext();
      const showTracking =
        business?.order_tracking_enabled === true &&
        result.orderId != null &&
        result.orderId !== '';
      const tu =
        typeof result.trackingUrl === 'string' && result.trackingUrl.trim() !== ''
          ? result.trackingUrl.trim()
          : null;
      setOrderPlaced({
        whatsappUrl: result.whatsappUrl || null,
        orderId: showTracking ? result.orderId : null,
        trackingUrl: tu,
        orderTextPlain: result.orderTextPlain || '',
        whatsappUnavailable: Boolean(result.whatsappUnavailable),
        totalSnap: typeof result.totalSnap === 'number' ? result.totalSnap : null,
      });
      clearCart();
      if (result.whatsappUrl) {
        openWhatsAppUrl(result.whatsappUrl, {
          businessId,
          source: 'assistant_checkout_auto',
        });
        toast.success('Pedido registrado — abriendo WhatsApp');
      } else {
        toast.success('Pedido registrado correctamente');
      }
    } finally {
      sendingRef.current = false;
      setSubmitting(false);
    }
  };

  const paymentLabel =
    getCheckoutPaymentLabel(paymentSelection, business) ||
    CHECKOUT_PAYMENT_LABELS[paymentSelection] ||
    canonicalPaymentMethod;
  const classicCheckoutLabelText = String(classicCheckoutLabel || 'checkout clásico');
  const standaloneClassicCheckoutLabel =
    classicCheckoutLabelText.charAt(0).toUpperCase() + classicCheckoutLabelText.slice(1);
  const headerTitle =
    step === 'cart' ? 'Lo que llevás' : step === 'data-preference' ? 'Tus datos' : title;
  const hideClassicCheckoutLink = checkoutBootstrap?.initialStep === 'cart';

  return (
    <div className="fixed inset-x-0 bottom-0 z-[70] max-h-[85vh] overflow-y-auto border-t bg-background text-foreground shadow-2xl rounded-t-xl">
      <div className="max-w-4xl mx-auto px-4 py-3 space-y-3">
        <div className="flex items-center justify-between gap-2">
          <h2 className="text-base font-semibold leading-snug pr-2">
            {headerTitle}
          </h2>
          <Button type="button" variant="ghost" size="icon" onClick={onClose} aria-label="Cerrar">
            <X className="w-5 h-5" />
          </Button>
        </div>

        {step === 'cart' && (
          <Card className="p-4 space-y-3 text-sm text-card-foreground">
            <p className="text-xs text-muted-foreground">
              Ajustá cantidades o quitá productos. Podés seguir agregando desde el menú.
            </p>
            <CheckoutCartLinesEditor
              cartLines={cartLines}
              updateQuantity={updateQuantity}
              removeFromCart={removeFromCart}
              removeAddOnFromCartLine={removeAddOnFromCartLine}
            />
            <p>
              <span className="font-medium">Subtotal:</span> ${subtotal.toLocaleString('es-CL')}
            </p>
            <div className="flex flex-col sm:flex-row flex-wrap gap-2 pt-1">
              <Button
                type="button"
                style={{ backgroundColor: primaryColor }}
                className="text-white"
                disabled={cartLines.length === 0}
                onClick={continueFromCart}
              >
                Continuar pedido
              </Button>
              <Button type="button" variant="outline" onClick={() => onClose()}>
                Seguir agregando
              </Button>
            </div>
          </Card>
        )}

        {step === 'data-preference' && (
          <Card className="p-4 space-y-3 text-card-foreground">
            <p className="text-sm text-muted-foreground">
              ¿Querés mantener tus datos anteriores (dirección y pago) o cambiarlos?
            </p>
            <div className="flex flex-col sm:flex-row flex-wrap gap-2">
              <Button
                type="button"
                style={{ backgroundColor: primaryColor }}
                className="text-white"
                onClick={applySavedCheckoutDefaults}
              >
                Mantener la información anterior
              </Button>
              <Button type="button" variant="outline" className="text-foreground" onClick={() => setStep('customer')}>
                Cambiar dirección o forma de pago
              </Button>
              <Button type="button" variant="ghost" className="text-foreground" onClick={() => setStep('service')}>
                Atrás
              </Button>
            </div>
          </Card>
        )}

        {step === 'service' && (
          <Card className="p-4 space-y-3 text-card-foreground">
            <p className="text-sm text-muted-foreground">¿Cómo querés recibirlo?</p>
            <RadioGroup
              value={serviceType}
              onValueChange={setServiceType}
              className="grid gap-2"
            >
              {serviceTypes.includes('delivery') ? (
                <label className="flex items-center gap-2 rounded-md border p-3 cursor-pointer">
                  <RadioGroupItem value="delivery" id="asst-svc-del" />
                  <MapPin className="w-4 h-4" />
                  <span>Delivery</span>
                </label>
              ) : null}
              {serviceTypes.includes('retiro') ? (
                <label className="flex items-center gap-2 rounded-md border p-3 cursor-pointer">
                  <RadioGroupItem value="retiro" id="asst-svc-ret" />
                  <span>Retiro en el local</span>
                </label>
              ) : null}
              {serviceTypes.includes('mesa') && business?.has_tables ? (
                <label className="flex items-center gap-2 rounded-md border p-3 cursor-pointer">
                  <RadioGroupItem value="mesa" id="asst-svc-mesa" />
                  <span>Mesa en el local</span>
                </label>
              ) : null}
            </RadioGroup>
            <div className="flex flex-wrap gap-2">
              <Button type="button" style={{ backgroundColor: primaryColor }} className="text-white" onClick={goAfterService}>
                Continuar
              </Button>
              {!hideClassicCheckoutLink ? (
                <Button
                  type="button"
                  variant="outline"
                  className="text-foreground"
                  onClick={() => goToClassicCheckout(navigate, publicMenuRouteBusinessId)}
                >
                  {classicCheckoutButtonLabel}
                </Button>
              ) : null}
            </div>
          </Card>
        )}

        {step === 'customer' && (
          <Card className="p-4 space-y-3">
            <p className="text-sm font-medium">Tus datos</p>
            <div className="grid gap-2 sm:grid-cols-2">
              <div className="space-y-1">
                <Label>Nombre</Label>
                <Input value={customerName} onChange={(e) => setCustomerName(e.target.value)} autoComplete="name" />
              </div>
              <div className="space-y-1">
                <Label>Teléfono</Label>
                <Input
                  value={customerPhone}
                  onChange={(e) => setCustomerPhone(e.target.value)}
                  type="tel"
                  autoComplete="tel"
                />
              </div>
            </div>
            <div className="space-y-1">
              <Label>Email (opcional)</Label>
              <Input
                value={customerEmail}
                onChange={(e) => setCustomerEmail(e.target.value)}
                type="email"
                autoComplete="email"
              />
            </div>
            {businessId ? (
              <CheckoutLoyaltySummary businessId={businessId} phone={customerPhone} />
            ) : null}
            <div className="flex flex-wrap gap-2">
              <Button type="button" variant="outline" onClick={() => setStep('service')}>
                Atrás
              </Button>
              <Button type="button" style={{ backgroundColor: primaryColor }} className="text-white" onClick={goAfterCustomer}>
                Continuar
              </Button>
            </div>
          </Card>
        )}

        {step === 'address' && (
          <Card className="p-4 space-y-3">
            <p className="text-sm font-medium">Dirección de envío</p>
            <GoogleMapsAddressInput
              address={address}
              setAddress={setAddress}
              location={markerPosition}
              setLocation={setMarkerPosition}
              placeholder="Calle, número, comuna"
              label="Dirección"
              mapHint="Ajustá el pin para cotizar el delivery."
              height="220px"
            />
            {useAddressZonesForDelivery ? (
              String(address || '').trim() ? (
                deliveryQuote?.no_zone_match ? (
                  <p className="text-sm font-medium text-amber-800 dark:text-amber-200 rounded-md bg-muted/60 p-3">
                    No tenemos cobertura para esta zona
                  </p>
                ) : typeof deliveryQuote?.delivery_fee === 'number' ? (
                  <div className="text-sm rounded-md bg-muted/60 p-3 space-y-1">
                    <p>
                      <span className="font-medium">Dirección:</span> {address || '—'}
                    </p>
                    {typeof deliveryQuote.estimated_minutes === 'number' && deliveryQuote.estimated_minutes > 0 ? (
                      <p>
                        <span className="font-medium">Tiempo aprox.:</span> {deliveryQuote.estimated_minutes} min
                      </p>
                    ) : null}
                    <p>
                      <span className="font-medium">Costo delivery:</span> $
                      {deliveryQuote.delivery_fee.toLocaleString('es-CL')}
                    </p>
                  </div>
                ) : null
              ) : null
            ) : deliveryLoading ? (
              <p className="text-xs text-muted-foreground flex items-center gap-2">
                <Loader2 className="w-3 h-3 animate-spin" /> Calculando envío…
              </p>
            ) : typeof deliveryQuote?.delivery_fee === 'number' ? (
              <div className="text-sm rounded-md bg-muted/60 p-3 space-y-1">
                <p>
                  <span className="font-medium">Dirección:</span> {address || '—'}
                </p>
                {deliveryQuote.distance_km != null ? (
                  <p>
                    <span className="font-medium">Distancia:</span> {deliveryQuote.distance_km.toFixed(2)} km
                  </p>
                ) : null}
                <p>
                  <span className="font-medium">Costo delivery:</span> $
                  {deliveryQuote.delivery_fee.toLocaleString('es-CL')}
                </p>
              </div>
            ) : markerPosition ? (
              <p className="text-xs text-amber-700">Ubicación lista; esperando cotización…</p>
            ) : null}
            <div className="flex flex-wrap gap-2">
              <Button type="button" variant="outline" onClick={() => setStep('customer')}>
                Atrás
              </Button>
              <Button type="button" style={{ backgroundColor: primaryColor }} className="text-white" onClick={goAfterAddress}>
                Continuar
              </Button>
            </div>
          </Card>
        )}

        {step === 'mesa' && (
          <Card className="p-4 space-y-3">
            <Label>Número de mesa</Label>
            <Input value={tableNumber} onChange={(e) => setTableNumber(e.target.value)} placeholder="Ej: 12" />
            <div className="flex flex-wrap gap-2">
              <Button type="button" variant="outline" onClick={() => setStep('customer')}>
                Atrás
              </Button>
              <Button type="button" style={{ backgroundColor: primaryColor }} className="text-white" onClick={goAfterMesa}>
                Continuar
              </Button>
            </div>
          </Card>
        )}

        {step === 'payment' && (
          <Card className="p-4 space-y-3">
            <p className="text-sm font-medium">Forma de pago</p>
              {paymentMethodsFallback && (
              <p
                className="text-xs font-medium text-amber-950 dark:text-amber-50 bg-amber-100 dark:bg-amber-900/50 border-2 border-amber-300 dark:border-amber-700 rounded-md p-2"
                role="status"
              >
                ⚠️ Este local no tiene métodos de pago configurados. Mostrando opciones por defecto.
              </p>
            )}
            <RadioGroup value={paymentSelection} onValueChange={setPaymentSelection} className="grid gap-2">
              {enabledPaymentMethods.map((id) => {
                const icon =
                  id === 'efectivo' ? (
                    <Banknote className="w-4 h-4" />
                  ) : id === 'transferencia' ? (
                    <Smartphone className="w-4 h-4" />
                  ) : id === 'online_manual_mercadopago' ? (
                    <Smartphone className="w-4 h-4" />
                  ) : (
                    <CreditCard className="w-4 h-4" />
                  );
                return (
                  <label
                    key={id}
                    className="flex items-center gap-2 rounded-md border p-3 cursor-pointer"
                    htmlFor={`asst-pay-${id}`}
                  >
                    <RadioGroupItem value={id} id={`asst-pay-${id}`} />
                    {icon}
                    <span>{getCheckoutPaymentLabel(id, business)}</span>
                  </label>
                );
              })}
            </RadioGroup>
            {paymentSelection === 'efectivo' ? (
              <div className="space-y-1">
                <Label>¿Con cuánto pagás? (opcional, para calcular vuelto)</Label>
                <Input
                  value={cashAmount}
                  onChange={(e) => setCashAmount(e.target.value)}
                  placeholder="Ej: 20000"
                  inputMode="numeric"
                />
              </div>
            ) : null}
            {paymentSelection === 'online_manual_mercadopago' ? (
              <p className="text-xs text-muted-foreground">
                {business?.mercadopago_online === true || business?.mp_oauth_connected === true
                  ? 'Al confirmar te redirigimos a Mercado Pago para pagar de forma segura. El cobro va a la cuenta del local.'
                  : 'El local te enviará un link o las instrucciones para pagar.'}
              </p>
            ) : null}
            <CheckoutTipSection tip={tip} setTip={setTip} primaryColor={primaryColor} />
            <div className="flex flex-wrap gap-2">
              <Button
                type="button"
                variant="outline"
                onClick={() => setStep(serviceType === 'delivery' ? 'address' : serviceType === 'mesa' ? 'mesa' : 'customer')}
              >
                Atrás
              </Button>
              <Button type="button" style={{ backgroundColor: primaryColor }} className="text-white" onClick={goAfterPayment}>
                Continuar
              </Button>
            </div>
          </Card>
        )}

        {step === 'proof' && (
          <Card className="p-4 space-y-4">
            <p className="text-sm font-medium">Transferencia bancaria</p>
            <div className="p-4 bg-secondary rounded-lg space-y-3">
              <div className="flex justify-between items-center gap-2">
                <h3 className="text-sm font-semibold">Datos bancarios</h3>
                <Button
                  type="button"
                  variant="outline"
                  size="sm"
                  className="shrink-0"
                  onClick={() => {
                    const bankData = `Banco: ${business.bank_name || '—'}
Tipo: ${labelBankAccountType(business.bank_account_type)}
Cuenta: ${business.bank_account_number || '—'}
RUT: ${business.bank_rut || '—'}
Titular: ${business.bank_holder_name || '—'}
Monto: $${total.toLocaleString('es-CL')}`;
                    navigator.clipboard.writeText(bankData);
                    toast.success('Datos bancarios copiados');
                  }}
                >
                  <Copy className="w-3 h-3 mr-1" /> Copiar todo
                </Button>
              </div>
              <div className="space-y-2 text-sm">
                <div className="flex justify-between gap-2">
                  <span className="text-muted-foreground shrink-0">Banco:</span>
                  <span className="font-medium text-right">{business.bank_name || '—'}</span>
                </div>
                <div className="flex justify-between gap-2">
                  <span className="text-muted-foreground shrink-0">Tipo de cuenta:</span>
                  <span className="font-medium text-right">{labelBankAccountType(business.bank_account_type)}</span>
                </div>
                <div className="flex justify-between items-center gap-2">
                  <span className="text-muted-foreground shrink-0">Número de cuenta:</span>
                  <div className="flex items-center gap-2 min-w-0">
                    <span className="font-medium font-mono text-right truncate">
                      {business.bank_account_number || '—'}
                    </span>
                    {business.bank_account_number ? (
                      <Button type="button" variant="ghost" size="icon" className="h-7 w-7 shrink-0" onClick={copyBankAccountNumber}>
                        <Copy className="w-3 h-3" />
                      </Button>
                    ) : null}
                  </div>
                </div>
                <div className="flex justify-between gap-2">
                  <span className="text-muted-foreground shrink-0">RUT:</span>
                  <span className="font-medium text-right">{business.bank_rut || '—'}</span>
                </div>
                <div className="flex justify-between gap-2">
                  <span className="text-muted-foreground shrink-0">Titular:</span>
                  <span className="font-medium text-right">{business.bank_holder_name || '—'}</span>
                </div>
                <p className="text-xs text-muted-foreground pt-1 border-t">
                  Monto a transferir: <span className="font-semibold text-foreground">${total.toLocaleString('es-CL')}</span>
                </p>
              </div>
            </div>

            <div className="space-y-2">
              <Label>Comprobante de transferencia</Label>
              <p className="text-xs text-muted-foreground">
                {uploadingProof
                  ? 'Subiendo imagen…'
                  : proofUrl
                    ? 'Podés tocar la imagen para elegir otra.'
                    : 'Tocá el recuadro para abrir la galería o la cámara.'}
              </p>
              <div className="relative mt-1 w-full max-w-sm min-h-[160px] rounded-lg border-2 border-dashed border-muted-foreground/40 bg-muted/25 overflow-hidden">
                <input
                  ref={proofInputRef}
                  type="file"
                  accept="image/*,application/pdf,.pdf"
                  className="absolute inset-0 z-20 h-full w-full min-h-[160px] cursor-pointer opacity-0 disabled:cursor-not-allowed"
                  onChange={handleProofSelect}
                  disabled={uploadingProof}
                  aria-label="Subir comprobante de transferencia"
                />
                {proofPreview ? (
                  <>
                    <img
                      src={proofPreview}
                      alt=""
                      className="pointer-events-none h-48 w-full object-cover"
                    />
                    {uploadingProof ? (
                      <div className="absolute inset-0 z-30 flex flex-col items-center justify-center gap-2 bg-black/55 text-white">
                        <Loader2 className="h-10 w-10 animate-spin" />
                        <span className="text-sm">Subiendo…</span>
                      </div>
                    ) : null}
                    {proofUrl && !uploadingProof ? (
                      <div className="pointer-events-none absolute bottom-2 left-2 z-10 rounded bg-green-600 px-2 py-1 text-xs text-white">
                        ✓ Listo para enviar
                      </div>
                    ) : null}
                    <Button
                      type="button"
                      variant="destructive"
                      size="icon"
                      className="absolute right-2 top-2 z-30 h-9 w-9 shadow-md"
                      onClick={(e) => {
                        e.preventDefault();
                        e.stopPropagation();
                        clearProofUpload();
                      }}
                      aria-label="Quitar comprobante"
                    >
                      <Trash2 className="h-4 w-4" />
                    </Button>
                  </>
                ) : (
                  <div className="pointer-events-none flex h-48 flex-col items-center justify-center gap-2 p-4 text-center">
                    <Upload className="h-10 w-10 text-muted-foreground" />
                    <span className="text-sm text-muted-foreground">Tocá para elegir imagen</span>
                  </div>
                )}
              </div>
            </div>

            <div className="flex flex-wrap gap-2">
              <Button type="button" variant="outline" onClick={() => setStep('payment')}>
                Atrás
              </Button>
              <Button
                type="button"
                style={{ backgroundColor: primaryColor }}
                className="text-white"
                disabled={uploadingProof || !proofUrl}
                onClick={() => setStep('review')}
              >
                Ver resumen
              </Button>
            </div>
          </Card>
        )}

        {step === 'review' && (
          <Card className="p-4 space-y-3 text-sm">
            <p className="font-semibold">Resumen del pedido</p>
            <p className="text-xs text-muted-foreground">
              Ajustá cantidades, sacá agregados o quitá un producto si te equivocaste. Todo queda guardado en el carrito.
            </p>
            <CheckoutCartLinesEditor
              cartLines={cartLines}
              updateQuantity={updateQuantity}
              removeFromCart={removeFromCart}
              removeAddOnFromCartLine={removeAddOnFromCartLine}
            />
            {orderNotes.trim() ? (
              <p>
                <span className="font-medium">Notas / horario:</span> {orderNotes.trim()}
              </p>
            ) : null}
            <p>
              <span className="font-medium">Servicio:</span>{' '}
              {serviceType === 'delivery' ? 'Delivery' : serviceType === 'retiro' ? 'Retiro' : `Mesa ${tableNumber}`}
            </p>
            {serviceType === 'delivery' ? (
              <>
                <p>
                  <span className="font-medium">Dirección:</span> {address}
                </p>
                {typeof deliveryQuote?.delivery_fee === 'number' ? (
                  <p>
                    <span className="font-medium">Delivery:</span> $
                    {deliveryQuote.delivery_fee.toLocaleString('es-CL')}
                    {deliveryQuote.distance_km != null ? ` (${deliveryQuote.distance_km.toFixed(2)} km)` : ''}
                  </p>
                ) : null}
              </>
            ) : null}
            <p>
              <span className="font-medium">Subtotal:</span> ${subtotal.toLocaleString('es-CL')}
            </p>
            {Number(tip) > 0 ? (
              <p>
                <span className="font-medium">Propina:</span> ${Number(tip).toLocaleString('es-CL')}
              </p>
            ) : null}
            <p>
              <span className="font-medium">Total:</span> ${total.toLocaleString('es-CL')}
            </p>
            <p>
              <span className="font-medium">Pago:</span> {paymentLabel}
            </p>
            {paymentSelection === 'efectivo' && parseCashAmountInput(cashAmount) != null ? (
              <p>
                <span className="font-medium">Paga con:</span> ${parseCashAmountInput(cashAmount).toLocaleString('es-CL')}
                {parseCashAmountInput(cashAmount) > total ? (
                  <span>
                    {' '}
                    — Vuelto: $
                    {(parseCashAmountInput(cashAmount) - total).toLocaleString('es-CL')}
                  </span>
                ) : null}
              </p>
            ) : null}
            {paymentSelection === 'transferencia' && proofUrl ? (
              <p className="text-xs break-all">
                <span className="font-medium">Comprobante:</span> {proofUrl}
              </p>
            ) : null}

            <div className="flex flex-col sm:flex-row flex-wrap gap-2 pt-2">
              <Button
                type="button"
                style={{ backgroundColor: primaryColor }}
                className="text-white"
                disabled={submitting}
                onClick={() => void confirmOrder()}
              >
                {submitting ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : null}
                {submitting ? 'Enviando…' : 'Enviar pedido por WhatsApp'}
              </Button>
              <Button type="button" variant="outline" onClick={() => setStep('payment')} disabled={submitting}>
                Editar pago
              </Button>
              <Button type="button" variant="outline" onClick={() => setStep('customer')} disabled={submitting}>
                Editar datos
              </Button>
              <Button type="button" variant="outline" onClick={() => onClose()} disabled={submitting}>
                Seguir eligiendo
              </Button>
              <Button
                type="button"
                variant="ghost"
                onClick={() => goToClassicCheckout(navigate, publicMenuRouteBusinessId)}
                disabled={submitting}
              >
                {standaloneClassicCheckoutLabel}
              </Button>
            </div>
          </Card>
        )}
      </div>
    </div>
  );
}

export function AssistantCheckoutFlow(props) {
  return (
    <AssistantCheckoutFlowBoundary
      open={props.open}
      businessId={props.businessId}
      publicMenuRouteBusinessId={props.publicMenuRouteBusinessId ?? ''}
      navigate={props.navigate}
      onClose={props.onClose}
      primaryColor={props.primaryColor}
      classicCheckoutButtonLabel={props.classicCheckoutButtonLabel}
    >
      <AssistantCheckoutFlowInner {...props} />
    </AssistantCheckoutFlowBoundary>
  );
}

export default AssistantCheckoutFlow;
